import {
  FIELD_TYPE_CATALOGUE,
  companyLockTargets,
  defaultContactSubFields,
  offersPostcodeLookup,
  visibilityText,
  type ConditionalVisibility,
  type FieldDef,
  type FieldType,
  type FieldTypeMeta,
  type FormType,
} from '@jfh/intake-shared';

/**
 * One page of the applicant's form: the `heading` that starts it (null for
 * questions before the first heading) and its questions. Same split as the
 * public renderer's `buildPages`, over customer questions only.
 */
export interface Step {
  head: FieldDef | null;
  items: FieldDef[];
}

/** The applicant's steps. Always at least one (empty) step. */
export function stepsOf(fields: FieldDef[]): Step[] {
  const steps: Step[] = [];
  let cur: Step | null = null;
  for (const f of fields) {
    if (f.audience === 'staff') continue;
    if (f.type === 'heading') {
      cur = { head: f, items: [] };
      steps.push(cur);
    } else {
      if (!cur) {
        cur = { head: null, items: [] };
        steps.push(cur);
      }
      cur.items.push(f);
    }
  }
  return steps.length ? steps : [{ head: null, items: [] }];
}

/** "1 question", "6 questions". */
export function questionCount(n: number): string {
  return `${n} ${n === 1 ? 'question' : 'questions'}`;
}

/** The step a question (or step heading) is on, or -1 (staff questions). */
export function stepIndexOf(fields: FieldDef[], fieldId: string): number {
  return stepsOf(fields).findIndex((s) => s.head?.id === fieldId || s.items.some((f) => f.id === fieldId));
}

/** Where a new question goes. */
export type InsertAt =
  | { beforeId: string }
  /** The end of a step (index into `stepsOf`). */
  | { step: number }
  /** A staff-only question, at the end. */
  | { staff: true };

/** The array index to insert a new question of `type` at. */
export function insertIndex(fields: FieldDef[], at: InsertAt, type: FieldType): number {
  if ('beforeId' in at) {
    const i = fields.findIndex((f) => f.id === at.beforeId);
    if (i >= 0) return i;
  }
  if ('staff' in at) return fields.length;
  const lastCustomer = fields.map((f) => f.audience).lastIndexOf('customer');
  // A new step goes after the applicant's last question.
  if (type === 'heading' || !('step' in at)) return lastCustomer + 1;
  const steps = stepsOf(fields);
  const st = steps[Math.min(Math.max(at.step, 0), steps.length - 1)]!;
  const last = st.items[st.items.length - 1] ?? st.head;
  return last ? fields.findIndex((f) => f.id === last.id) + 1 : 0;
}

/**
 * Move a question one place among questions with the same audience (staff
 * questions sit in their own list). An applicant question can't move above a
 * leading step heading. Returns null when it can't move.
 */
export function moveQuestion(fields: FieldDef[], fieldId: string, dir: -1 | 1): FieldDef[] | null {
  const i = fields.findIndex((f) => f.id === fieldId);
  if (i < 0) return null;
  const aud = fields[i]!.audience;
  let j = i + dir;
  while (j >= 0 && j < fields.length && fields[j]!.audience !== aud) j += dir;
  if (j < 0 || j >= fields.length) return null;
  if (aud === 'customer' && j === 0 && fields[0]!.type === 'heading' && fields[i]!.type !== 'heading') return null;
  const arr = [...fields];
  const [moved] = arr.splice(i, 1);
  arr.splice(j, 0, moved!);
  return arr;
}

/**
 * Move a whole step (its heading and questions) so it lands at index `to` of
 * `stepsOf`. Staff questions keep their places. Questions before the first
 * heading have no heading to carry them, so that step stays first. Returns
 * null when it can't move.
 */
export function moveStep(fields: FieldDef[], from: number, to: number): FieldDef[] | null {
  const steps = stepsOf(fields);
  if (from === to || !steps[from] || !steps[to]) return null;
  if (!steps[0]!.head && (from === 0 || to === 0)) return null;
  const blocks = steps.map((s) => (s.head ? [s.head, ...s.items] : s.items));
  const [moved] = blocks.splice(from, 1);
  blocks.splice(to, 0, moved!);
  const order = blocks.flat();
  let k = 0;
  return fields.map((f) => (f.audience === 'staff' ? f : order[k++]!));
}

/** A new question, with sensible starting wording and answers. */
export function newQuestion(type: FieldType, audience: FieldDef['audience'], fields: FieldDef[]): FieldDef {
  const f: FieldDef = {
    id: crypto.randomUUID().slice(0, 8),
    type,
    label: type === 'heading' ? 'New step' : type === 'consent' ? 'I agree to the terms' : 'New question',
    audience,
    required: false,
  };
  if (['select', 'radio', 'multiselect'].includes(type)) f.options = ['Answer 1', 'Answer 2'];
  if (type === 'contact') {
    f.subFields = defaultContactSubFields();
    f.itemLabel = 'Contact';
    f.minItems = 1;
  }
  if (type === 'company_number') {
    f.label = 'Company registration number';
    // Link the obvious targets: they can be changed in the panel.
    const name = fields.find((x) => x.type === 'short_text' && /company name/i.test(x.label));
    const address = fields.find((x) => x.type === 'address' && /registered/i.test(x.label));
    if (name || address) {
      f.companyLookup = {
        ...(name ? { nameFieldId: name.id } : {}),
        ...(address ? { addressFieldId: address.id } : {}),
      };
    }
  }
  return f;
}

/** The add-a-question picker's groups (the stored types don't change). */
export const PICKER_GROUPS: { label: string; types: FieldType[] }[] = [
  { label: 'Text and numbers', types: ['short_text', 'long_text', 'email', 'phone', 'number', 'date'] },
  { label: 'Choices', types: ['select', 'radio', 'multiselect', 'checkbox'] },
  {
    label: 'Business details',
    types: ['company_number', 'vat', 'sort_code', 'address', 'contact', 'repeater', 'file'],
  },
  { label: 'Steps and agreements', types: ['heading', 'consent', 'hidden'] },
];

/** Types staff can't add as a staff-only question. */
const NOT_FOR_STAFF: FieldType[] = ['heading', 'consent', 'company_number'];

/** The picker's groups matching a search, with the catalogue's plain names. */
export function pickerGroups(query: string, staffView: boolean): { label: string; items: FieldTypeMeta[] }[] {
  const q = query.trim().toLowerCase();
  return PICKER_GROUPS.map((g) => ({
    label: g.label,
    items: g.types
      .filter((t) => !(staffView && NOT_FOR_STAFF.includes(t)))
      .map((t) => FIELD_TYPE_CATALOGUE.find((m) => m.type === t))
      .filter((m): m is FieldTypeMeta => Boolean(m))
      .filter((m) => !q || `${m.plainName} ${m.description} ${m.example} ${m.label} ${m.type}`.toLowerCase().includes(q)),
  })).filter((g) => g.items.length > 0);
}

export type ChipTone = 'info' | 'ok' | 'warn' | 'plain';
export interface Chip {
  icon: string;
  text: string;
  tone: ChipTone;
}

/** The notes under a question on the canvas: rules, look-ups, staff details. */
export function questionChips(f: FieldDef, fields: FieldDef[], formType: FormType): Chip[] {
  const chips: Chip[] = [];
  const vis = f.visibleWhen as ConditionalVisibility | undefined;
  if (vis?.rules?.length) chips.push({ icon: 'filter', text: `Only shown when ${visibilityText(vis, fields)}`, tone: 'info' });
  const lockedBy = companyLockTargets(fields).get(f.id);
  const lockField = lockedBy ? fields.find((x) => x.id === lockedBy) : undefined;
  if (lockField) chips.push({ icon: 'shield', text: `Filled in and locked by “${lockField.label}”`, tone: 'ok' });
  if (f.type === 'company_number') {
    const names = [f.companyLookup?.nameFieldId, f.companyLookup?.addressFieldId]
      .map((id) => fields.find((x) => x.id === id))
      .filter((x): x is FieldDef => Boolean(x))
      .map((x) => `“${x.label}”`);
    chips.push(
      names.length
        ? { icon: 'badge', text: `Checks Companies House, fills ${names.join(' and ')}`, tone: 'ok' }
        : { icon: 'flag', text: 'Choose which questions it fills in', tone: 'warn' },
    );
  }
  const credit = formType === 'credit_account';
  if (f.audience === 'staff' && credit) {
    chips.push({
      icon: 'user',
      text: f.staffStage === 'accounts' ? 'Accounts (stage 2)' : 'Customer Service (stage 1)',
      tone: 'plain',
    });
  }
  if (f.answerFirst && f.audience === 'customer') {
    chips.push({ icon: 'filter', text: 'The rest of this step appears once it’s answered', tone: 'info' });
  }
  if (f.type === 'address' && offersPostcodeLookup(f.address)) {
    chips.push({ icon: 'search', text: 'Find by postcode', tone: 'plain' });
  }
  const from = f.type === 'address' ? fields.find((x) => x.id === f.address?.countryFrom) : undefined;
  if (from) chips.push({ icon: 'pin', text: `Country starts as the answer to “${from.label}”`, tone: 'plain' });
  if (f.optionsSource) chips.push({ icon: 'db', text: 'Answers from iQ', tone: 'plain' });
  if (f.cardSummary && credit) chips.push({ icon: 'teams', text: 'Highlighted on the Accounts Teams card', tone: 'plain' });
  return chips;
}

/** Where the picker will put the new question, in words. */
export function insertWhere(fields: FieldDef[], at: InsertAt): string {
  if ('beforeId' in at) {
    const f = fields.find((x) => x.id === at.beforeId);
    if (f) return `It will go just above “${f.label}”.`;
  }
  if ('staff' in at) return 'It will be a staff-only question. Applicants won’t see it.';
  const steps = stepsOf(fields);
  const st = 'step' in at ? steps[Math.min(at.step, steps.length - 1)] : undefined;
  return `It will go at the end of “${st?.head?.label ?? 'this step'}”. A new step goes after the last one.`;
}
